import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errText } from "../../lib/admin";
import { Btn, Empty, Loading, Pill, Field, field } from "./ui";
import { ImageField, GalleryField } from "./ImageField";
import { slugify } from "./PostsAdmin";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const pairs = (s) => lines(s).map((l) => { const [a, ...b] = l.split("|"); return [a.trim(), b.join("|").trim()]; }).filter(([a]) => a);
const pairsText = (xs = []) => xs.map(([a, b]) => `${a} | ${b}`).join("\n");

const toForm = (t = {}) => ({
  name: t.name || "", line: t.line || "", tagline: t.tagline || "", claim: t.claim || "",
  tags: (t.tags || []).join(", "), benefits: (t.benefits || []).join("\n"),
  ingredientsTitle: t.ingredientsTitle || "", ingredientsSub: t.ingredientsSub || "", ingredients: pairsText(t.ingredients),
  resultsTitle: t.resultsTitle || "", resultsSub: t.resultsSub || "", results: pairsText(t.results),
});
const fromForm = (t) => ({
  ...t, tags: t.tags.split(",").map((x) => x.trim()).filter(Boolean), benefits: lines(t.benefits),
  ingredients: pairs(t.ingredients), results: pairs(t.results),
});

const TextFields = ({ t, set, lang }) => {
  const tid = (k) => `product-${lang}-${k}-input`;
  const inp = (k, label, req) => <Field label={label}><input required={req} className={field} value={t[k]} onChange={set(k)} data-testid={tid(k)} /></Field>;
  const area = (k, label, hint, rows = 4) => <Field label={label} hint={hint}><textarea rows={rows} className={field} value={t[k]} onChange={set(k)} data-testid={tid(k)} /></Field>;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">{inp("name", "Nome prodotto", lang === "it")}{inp("line", "Linea (es. Gioia Skincare · Viso)")}</div>
      {inp("tagline", "Frase principale")}
      {inp("tags", "Etichette (separate da virgola)")}
      {area("benefits", "Benefici", "Uno per riga")}
      <div className="grid sm:grid-cols-2 gap-4">{inp("ingredientsTitle", "Titolo sezione ingredienti")}{inp("ingredientsSub", "Sottotitolo ingredienti")}</div>
      {area("ingredients", "Ingredienti", "Uno per riga: Nome | descrizione", 5)}
      <div className="grid sm:grid-cols-2 gap-4">{inp("resultsTitle", "Titolo sezione risultati")}{inp("resultsSub", "Sottotitolo risultati")}</div>
      {area("results", "Risultati", "Uno per riga: Titolo | descrizione", 4)}
      {inp("claim", "Frase finale")}
    </div>
  );
};

const EMPTY = { slug: "", img: "", gallery: [], order: 99, hidden: false, it: {}, en: {} };

const Editor = ({ product, onClose, onSaved }) => {
  const isNew = !product.slug;
  const [f, setF] = useState({ slug: product.slug, img: product.img, gallery: product.gallery || [], order: product.order ?? 99, hidden: !!product.hidden });
  const [txt, setTxt] = useState({ it: toForm(product.it), en: toForm(product.en) });
  const [lang, setLang] = useState("it");
  const [saving, setSaving] = useState(false);
  const setT = (k) => (e) => setTxt({ ...txt, [lang]: { ...txt[lang], [k]: e.target.value } });
  const save = async (e) => {
    e.preventDefault();
    if (!txt.it.name.trim()) { setLang("it"); toast.error("Inserisci il nome in italiano"); return; }
    if (!f.img) { toast.error("Carica la foto principale"); return; }
    const body = { ...f, slug: f.slug || slugify(txt.it.name), it: fromForm(txt.it), en: txt.en.name.trim() ? fromForm(txt.en) : null };
    setSaving(true);
    try {
      const { data } = isNew ? await api.post("/admin/products", body) : await api.put(`/admin/products/${product.slug}`, body);
      toast.success(isNew ? "Prodotto aggiunto allo Shop" : "Prodotto salvato");
      onSaved(data, product.slug);
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };
  return (
    <form onSubmit={save} className="space-y-5" data-testid="product-editor-form">
      <Field label="Indirizzo nello Shop" hint={`/shop#${f.slug || slugify(txt.it.name) || "…"}`}>
        <input className={field} value={f.slug} placeholder={slugify(txt.it.name)} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="product-slug-input" />
      </Field>
      <Field label="Foto principale"><ImageField value={f.img} onChange={(img) => setF({ ...f, img })} testId="product-img" /></Field>
      <Field label="Altre foto (galleria)"><GalleryField value={f.gallery} onChange={(gallery) => setF({ ...f, gallery })} testId="product-gallery" /></Field>
      <div className="flex gap-2 border-b border-[#e7dfd3]">
        {[["it", "Italiano"], ["en", "English (facoltativo)"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setLang(k)} data-testid={`product-lang-${k}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm transition-colors duration-200 ${lang === k ? "border-[#8A6A3A] text-[#14110F]" : "border-transparent text-[#6b625a] hover:text-[#14110F]"}`}>{l}</button>
        ))}
      </div>
      {lang === "en" && <p className="text-xs text-[#6b625a]">Se lasci vuoto il nome inglese, sul sito in inglese verranno mostrati i testi italiani.</p>}
      <TextFields key={lang} t={txt[lang]} set={setT} lang={lang} />
      <div className="grid sm:grid-cols-2 gap-4 items-end">
        <Field label="Posizione nello Shop" hint="1 = primo"><input type="number" min={0} className={field} value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} data-testid="product-order-input" /></Field>
        <label className="flex items-center gap-3 text-sm text-[#14110F] cursor-pointer pb-3">
          <input type="checkbox" checked={f.hidden} onChange={(e) => setF({ ...f, hidden: e.target.checked })} className="w-4 h-4 accent-[#8A6A3A]" data-testid="product-hidden-checkbox" /> Nascondi dallo Shop
        </label>
      </div>
      <div className="flex flex-wrap gap-3 pt-2">
        <Btn type="submit" loading={saving} data-testid="product-save-btn">{isNew ? "Aggiungi allo Shop" : "Salva modifiche"}</Btn>
        <Btn variant="ghost" onClick={onClose} data-testid="product-cancel-btn">Annulla</Btn>
      </div>
    </form>
  );
};

const Row = ({ p, onEdit, onDelete }) => (
  <article data-testid={`product-row-${p.slug}`} className="bg-white border border-[#e7dfd3] rounded-xl p-3 sm:p-4 flex gap-4 items-center">
    <div className="w-16 h-20 rounded-lg overflow-hidden bg-[#efe9df] shrink-0"><img src={p.img} alt="" className="w-full h-full object-cover" /></div>
    <div className="min-w-0 flex-1">
      <h3 className="font-serif-d text-xl font-light text-[#14110F] leading-snug">{p.it?.name}</h3>
      <p className="text-sm text-[#6b625a] truncate">{p.it?.line}</p>
      {p.hidden && <span className="mt-1 inline-flex"><Pill tone="neutral"><EyeOff className="w-3 h-3 mr-1" /> Nascosto</Pill></span>}
    </div>
    <button type="button" aria-label="Modifica" onClick={() => onEdit(p)} className="p-2 text-[#6b625a] hover:text-[#8A6A3A]" data-testid={`product-edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
    <button type="button" aria-label="Elimina" onClick={() => onDelete(p)} className="p-2 text-[#6b625a] hover:text-[#9a3b2b]" data-testid={`product-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
  </article>
);

export const ProductsAdmin = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.get("/admin/products").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, []);
  const onSaved = (doc, oldSlug) => {
    setItems((xs) => [...xs.filter((x) => x.slug !== (oldSlug || doc.slug)), doc].sort((a, b) => a.order - b.order));
    setEditing(null);
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.it?.name}" dallo Shop?`)) return;
    try { await api.delete(`/admin/products/${p.slug}`); setItems((xs) => xs.filter((x) => x.slug !== p.slug)); toast.success("Prodotto eliminato"); } catch (e) { toast.error(errText(e)); }
  };
  if (!items) return <Loading />;
  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[#4f4841]">{items.length} prodotti</p>
        <Btn variant="gold" onClick={() => setEditing({ ...EMPTY, order: items.length })} data-testid="product-new-btn"><Plus className="w-4 h-4" /> Nuovo prodotto</Btn>
      </div>
      {items.length ? <div className="space-y-3">{items.map((p) => <Row key={p.slug} p={p} onEdit={setEditing} onDelete={onDelete} />)}</div> : <Empty>Nessun prodotto.</Empty>}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="bg-[#F6F2EC] max-w-[95vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-editor-dialog">
          <DialogTitle className="font-serif-d font-light text-3xl text-[#14110F]">{editing?.slug ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
          <DialogDescription className="text-sm text-[#6b625a]">Il pulsante "Chiedi info" su WhatsApp viene creato in automatico.</DialogDescription>
          {editing && <Editor key={editing.slug || "new"} product={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
